/**
 * Coordinates one active speech feature per renderer window. Activity in a
 * separate Hummingbird window can still overlap the main window.
 */
import { observable, runInAction } from 'mobx';

// NOTE: Editing this file while the app runs can leave two live copies in
// dev, each coordinating separately (the two-copy problem in
// ai-rules/desktop.md#module-state-across-hot-reload-copies). Not observed in
// practice; apply the rule's globalThis pattern if it ever is.
let currentSpeechActivity: { stop: () => void } | null = null;
// Observable so the update-activity reporter picks speech up in the same
// snapshot as chat and voice instead of pushing it separately.
const speechActivityActive = observable.box(false);

/**
 * Stop the previous speech feature, install this feature's stop callback, and
 * return a release function that cannot clear a newer feature's claim.
 */
export function claimSpeechActivity(stopWhenReplaced: () => void): () => void {
  // TODO: A stop callback that throws leaves this claim uninstalled, so the
  // new feature runs without owning the slot
  currentSpeechActivity?.stop();
  const claim = { stop: stopWhenReplaced };
  currentSpeechActivity = claim;
  runInAction(() => speechActivityActive.set(true));

  return function releaseSpeechActivity() {
    // Late cleanup from a replaced feature must not unregister its replacement
    if (currentSpeechActivity === claim) {
      currentSpeechActivity = null;
      runInAction(() => speechActivityActive.set(false));
    }
  };
}

export function isSpeechActivityActive(): boolean {
  return speechActivityActive.get();
}
